import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await login(email, pw);
      toast.success("Welcome back!");
      const roleDest = {
        admin: "/admin",
        teacher: "/teacher/dashboard",
        parent: "/parent/dashboard",
        partner: "/partner/dashboard",
      }[user.role] || "/dashboard";
      const dest = loc.state?.from || roleDest;
      nav(dest, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid email or password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md bg-white border border-[#E5E7EB] rounded-2xl p-8">
        <div className="label-eyebrow">Welcome back</div>
        <h1 className="font-display font-black text-3xl mt-2">Sign in</h1>
        <p className="text-sm text-[#4B5563] mt-2">Continue your learning journey.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-sm font-semibold">Email</label>
            <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#D92D20]" />
          </div>
          <div>
            <label className="text-sm font-semibold">Password</label>
            <input data-testid="login-password" type="password" required value={pw} onChange={(e) => setPw(e.target.value)}
              className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#D92D20]" />
          </div>
          <button data-testid="login-submit" type="submit" disabled={busy} className="btn-primary w-full">
            {busy ? "Signing in..." : "Sign in"}
          </button>
        </form>
        <div className="text-sm mt-4 text-center">
          <Link to="/forgot-password" data-testid="link-forgot" className="text-[#4B5563] hover:text-[#D92D20]">Forgot your password?</Link>
        </div>
        <p className="text-sm mt-3 text-center text-[#4B5563]">
          New here? <Link to="/register" className="text-[#D92D20] font-semibold" data-testid="link-register">Create an account</Link>
        </p>

        <div className="mt-6 pt-5 border-t border-[#E5E7EB]">
          <p className="text-[11px] uppercase tracking-wider text-[#6B7280] text-center mb-2 font-semibold">
            Quick sign-up shortcuts
          </p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <Link to="/register?role=student" data-testid="quick-register-student"
              className="border border-[#E5E7EB] rounded-md py-2 text-center hover:border-[#DC2626] hover:text-[#DC2626]">
              🎓 Student
            </Link>
            <Link to="/register?role=parent" data-testid="quick-register-parent"
              className="border border-[#E5E7EB] rounded-md py-2 text-center hover:border-[#064E3B] hover:text-[#064E3B]">
              👨‍👩‍👧 Parent
            </Link>
            <Link to="/register?role=teacher" data-testid="quick-register-teacher"
              className="border border-[#E5E7EB] rounded-md py-2 text-center hover:border-[#0F766E] hover:text-[#0F766E]">
              🏫 Teacher
            </Link>
            <Link to="/register?role=partner" data-testid="quick-register-partner"
              className="border border-[#E5E7EB] rounded-md py-2 text-center hover:border-[#F59E0B] hover:text-[#B45309]">
              🏢 Partner
            </Link>
          </div>
          <p className="text-[11px] text-center text-[#9CA3AF] mt-3">
            One account, every role. Pick what fits you today.
          </p>
        </div>
      </div>
    </div>
  );
}
